import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Business as BusinessIcon,
  Language as DomainIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
} from '@mui/icons-material';
import {
  Add_Company_Domain_Action,
  GetAll_Company_Domains_Action,
  UpdateCompanyDomainData,
  Delete_Company_Domain_Action,
} from '../Redux/Actions/CompanyDomains_Actions';
import { useSidebarWidth } from '../Utility/useSidebarWidth';

/* ─── Inline Icons ───────────────────────────────────────────────────────── */
const IconSearch   = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
const IconFilter   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>;
const IconPlus     = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IconEdit     = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IconTrash    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>;
const IconGrid     = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>;
const IconList     = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>;
const IconClose    = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IconWarning  = () => <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
const IconBuilding = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9.5L12 3l9 6.5V21H3V9.5z"/><rect x="9" y="14" width="6" height="7" rx="1"/></svg>;
const IconGlobe    = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10A15.3 15.3 0 018 12a15.3 15.3 0 014-10z"/></svg>;
const IconTrend    = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;

export const AllCompanyDomains = () => {
  const theme      = useTheme();
  const isDark     = theme.palette.mode === 'dark';
  const dispatch   = useDispatch();
  const sidebarWidth = useSidebarWidth();

  /* ── Redux ── */
  const { Companydomain, loading, error } = useSelector((state) => state.AllCompanyDomains || {
    Companydomain: { data: [], pagination: {} },
    loading: true,
    error: null,
  });

  /* ── State ── */
  const [viewMode,       setViewMode]       = useState('grid');
  const [page,           setPage]           = useState(1);
  const [rowsPerPage,    setRowsPerPage]    = useState(10);
  const [searchName,     setSearchName]     = useState('');
  const [searchDomain,   setSearchDomain]   = useState('');
  const [filterActive,   setFilterActive]   = useState('');
  const [sortBy,         setSortBy]         = useState('createdAt');
  const [sortOrder,      setSortOrder]      = useState('desc');
  const [isSubmitting,   setIsSubmitting]   = useState(false);
  const [localLoading,   setLocalLoading]   = useState(false);
  const [showFilter,     setShowFilter]     = useState(false);

  const [openDialog,       setOpenDialog]       = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedCompany,  setSelectedCompany]  = useState(null);
  const [formData,         setFormData]         = useState({ company_name: '', company_domain: '', is_active: true });
  const [formErrors,       setFormErrors]       = useState({});

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  /* ── Fetch ── */
  useEffect(() => { fetchCompanies(); }, [page, rowsPerPage, searchName, searchDomain, filterActive, sortBy, sortOrder]);

  const fetchCompanies = async () => {
    setLocalLoading(true);
    const q = new URLSearchParams({
      page,
      limit: rowsPerPage,
      ...(searchName   && { company_name:   searchName }),
      ...(searchDomain && { company_domain: searchDomain }),
      ...(filterActive !== '' && { is_active: filterActive }),
      sort_by:    sortBy,
      sort_order: sortOrder,
    });
    try {
      await dispatch(GetAll_Company_Domains_Action(q.toString()));
    } catch {
      setSnackbar({ open: true, message: 'Failed to fetch companies', severity: 'error' });
    } finally {
      setLocalLoading(false);
    }
  };

  const companiesList  = Companydomain?.data || [];
  const paginationData = Companydomain?.pagination || { total: 0 };
  const isLoading      = loading || localLoading;

  /* ── Validate ── */
  const validateForm = () => {
    const errors = {};
    if (!formData.company_name?.trim())   errors.company_name   = 'Company name is required';
    if (!formData.company_domain?.trim()) errors.company_domain = 'Company domain is required';
    else if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.company_domain))
      errors.company_domain = 'Enter a valid domain (e.g., example.com)';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* ── Submit ── */
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const body = { company_name: formData.company_name, company_domain: formData.company_domain, is_active: formData.is_active };
      if (selectedCompany) {
        await dispatch(UpdateCompanyDomainData(selectedCompany._id, body));
        setSnackbar({ open: true, message: 'Company updated successfully!', severity: 'success' });
      } else {
        await dispatch(Add_Company_Domain_Action(body));
        setSnackbar({ open: true, message: 'Company created successfully!', severity: 'success' });
      }
      handleCloseDialog();
      fetchCompanies();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'An error occurred', severity: 'error' });
    } finally { setIsSubmitting(false); }
  };

  const handleEdit = (company) => {
    setSelectedCompany(company);
    setFormData({ company_name: company.company_name, company_domain: company.company_domain, is_active: company.is_active });
    setOpenDialog(true);
  };

  const handleDeleteClick = (company) => { setSelectedCompany(company); setOpenDeleteDialog(true); };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await dispatch(Delete_Company_Domain_Action(selectedCompany._id, { is_active: false }));
      setSnackbar({ open: true, message: 'Company deleted successfully!', severity: 'success' });
      setOpenDeleteDialog(false);
      fetchCompanies();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Error deleting company', severity: 'error' });
    } finally { setIsSubmitting(false); }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCompany(null);
    setFormData({ company_name: '', company_domain: '', is_active: true });
    setFormErrors({});
  };

  const clearFilters = () => { setSearchName(''); setSearchDomain(''); setFilterActive(''); setSortBy('createdAt'); setSortOrder('desc'); setPage(1); };

  const totalPages  = Math.ceil((paginationData.total || 0) / rowsPerPage);
  const activeCount = companiesList.filter(c => c.is_active).length;
  const inactiveCount = companiesList.filter(c => !c.is_active).length;

  /* ── shared style helpers ── */
  const inputSt = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '8px',
    border: `1.5px solid ${isDark ? '#3a3a5a' : '#e0e2ef'}`,
    background: isDark ? '#252540' : '#fff',
    color: isDark ? '#e0e0ff' : '#1f2937',
    fontSize: '13px',
    outline: 'none',
    fontFamily: "'DM Sans', sans-serif",
    boxSizing: 'border-box',
  };

  const dialogInputSt = (hasErr) => ({
    width: '100%',
    padding: '11px 14px',
    borderRadius: '10px',
    border: `1.5px solid ${hasErr ? '#ef4444' : (isDark ? '#2d3248' : '#e2e8f0')}`,
    backgroundColor: isDark ? '#151827' : '#ffffff',
    color: isDark ? '#f1f5f9' : '#0f172a',
    fontSize: '14px',
    outline: 'none',
    fontFamily: "'DM Sans', sans-serif",
    boxSizing: 'border-box',
    transition: 'all 0.2s',
  });

  const labelSt = {
    display: 'block',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: isDark ? '#9ca3af' : '#64748b',
    marginBottom: '6px',
    fontFamily: "'DM Sans', sans-serif",
  };

  /* ─────────────────────────────────────────────────────────────────────── */
  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:none; } }
        .domain-card { animation: fadeUp 0.25s ease both; }
        .domain-card:hover { transform: translateY(-4px) !important; box-shadow: 0 12px 28px rgba(0,0,0,0.14) !important; }
        .list-row:hover { background: ${isDark ? '#1d2040' : '#f5f7ff'} !important; }
        .ap-action-btn { width:30px; height:30px; border-radius:8px; border:1px solid ${isDark ? '#2d3248' : '#e2e8f0'}; background:transparent; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; color:${isDark ? '#9ca3af' : '#6b7280'}; transition:all 0.15s; }
        .ap-action-btn:hover { background:${isDark ? '#252545' : '#f0f2ff'}; color:${isDark ? '#c0c0e0' : '#1d4ed8'}; }
        .ap-action-btn.red:hover { background:#fef2f2; color:#dc2626; }
        @keyframes pm-fade  { from { opacity:0; } to { opacity:1; } }
        @keyframes pm-slide { from { opacity:0; transform:translateY(20px) scale(0.97); } to { opacity:1; transform:none; } }
        @keyframes pm-spin  { to { transform:rotate(360deg); } }
      `}</style>

      <div style={{
        position: 'fixed',
        top: '50px',
        left: sidebarWidth,
        right: 0,
        bottom: 0,
        transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      }}>

        {/* ── Breadcrumb ── */}
        <section style={{ padding: '16px 20px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <Link to="/AllLeadsForAdmin" style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>Home</Link>
            <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
            <span style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: 600 }}>Company Domains</span>
          </div>
        </section>

        {/* ── Stats ── */}
        <section style={{ padding: '20px 20px 0', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '16px', flexShrink: 0 }}>
          {/* Total */}
          <div style={{ background: isDark ? '#1e2132' : '#fff', borderRadius: '14px', padding: '20px 24px', border: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: isDark ? '#6b7280' : '#94a3b8', marginBottom: '6px', textTransform: 'uppercase' }}>TOTAL DOMAINS</div>
              <div style={{ fontSize: '32px', fontWeight: 800, color: isDark ? '#f1f5f9' : '#0f172a', lineHeight: 1 }}>{paginationData.total || companiesList.length}</div>
              <div style={{ marginTop: '8px', display: 'inline-block', fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '999px', background: isDark ? '#1d2a5e' : '#eef1fb', color: isDark ? '#90b4ff' : '#0d2a6e' }}>All entries</div>
            </div>
            <div style={{ width: 46, height: 46, borderRadius: '12px', background: isDark ? '#252d4a' : '#f0f4ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDark ? '#7da4ff' : '#1d4ed8' }}>
              <IconBuilding />
            </div>
          </div>

          {/* Active */}
          <div style={{ background: isDark ? '#1e2132' : '#fff', borderRadius: '14px', padding: '20px 24px', border: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: isDark ? '#6b7280' : '#94a3b8', marginBottom: '6px', textTransform: 'uppercase' }}>ACTIVE</div>
              <div style={{ fontSize: '32px', fontWeight: 800, color: isDark ? '#f1f5f9' : '#0f172a', lineHeight: 1 }}>{activeCount}</div>
              <div style={{ marginTop: '8px', display: 'inline-block', fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '999px', background: isDark ? '#14402a' : '#dcfce7', color: isDark ? '#4ade80' : '#16a34a' }}>↑ Running now</div>
            </div>
            <div style={{ width: 46, height: 46, borderRadius: '12px', background: isDark ? '#14402a' : '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDark ? '#4ade80' : '#16a34a' }}>
              <IconGlobe />
            </div>
          </div>

          {/* Filtered */}
          <div style={{ background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)', borderRadius: '14px', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.6)', marginBottom: '6px', textTransform: 'uppercase' }}>INACTIVE</div>
              <div style={{ fontSize: '32px', fontWeight: 800, color: '#fff', lineHeight: 1 }}>{inactiveCount}</div>
              <div style={{ marginTop: '8px', display: 'inline-block', fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '999px', background: 'rgba(255,255,255,0.18)', color: 'rgba(255,255,255,0.9)' }}>Paused</div>
            </div>
            <div style={{ width: 46, height: 46, borderRadius: '12px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <IconTrend />
            </div>
          </div>
        </section>

        {/* ── Table Section ── */}
        <section style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, margin: '20px 20px 0' }}>

          {/* Table Header */}
          <div style={{
            background: isDark ? '#1e2132' : '#fff',
            borderRadius: '14px 14px 0 0',
            padding: '16px 20px',
            border: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`,
            borderBottom: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap',
            flexShrink: 0,
          }}>
            {/* Left: title + view toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: isDark ? '#6b7280' : '#94a3b8' }}>DIRECTORY</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>COMPANY DOMAINS</div>
              </div>
              {/* View toggle */}
              <div style={{ display: 'flex', gap: '4px', background: isDark ? '#151827' : '#f0f2f5', borderRadius: '8px', padding: '3px' }}>
                <button onClick={() => setViewMode('grid')} style={{ padding: '5px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', background: viewMode === 'grid' ? (isDark ? '#252d4a' : '#fff') : 'transparent', color: viewMode === 'grid' ? (isDark ? '#7da4ff' : '#1d4ed8') : (isDark ? '#6b7280' : '#94a3b8'), display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, fontFamily: "'DM Sans',sans-serif", boxShadow: viewMode === 'grid' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>
                  <IconGrid /> Grid
                </button>
                <button onClick={() => setViewMode('list')} style={{ padding: '5px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', background: viewMode === 'list' ? (isDark ? '#252d4a' : '#fff') : 'transparent', color: viewMode === 'list' ? (isDark ? '#7da4ff' : '#1d4ed8') : (isDark ? '#6b7280' : '#94a3b8'), display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, fontFamily: "'DM Sans',sans-serif", boxShadow: viewMode === 'list' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>
                  <IconList /> List
                </button>
              </div>
            </div>

            {/* Right: search + filter + add */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {/* Search */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '7px 12px', borderRadius: '8px', border: `1.5px solid ${isDark ? '#3a3a5a' : '#e0e2ef'}`, background: isDark ? '#252540' : '#f9fafb', minWidth: '220px' }}>
                <span style={{ color: isDark ? '#6b7280' : '#9ca3af' }}><IconSearch /></span>
                <input
                  placeholder="Search name or domain…"
                  value={searchName}
                  onChange={(e) => { setSearchName(e.target.value); setPage(1); }}
                  style={{ border: 'none', background: 'none', outline: 'none', fontSize: '13px', color: isDark ? '#e0e0ff' : '#1f2937', fontFamily: "'DM Sans',sans-serif", width: '100%' }}
                />
                {searchName && <button onClick={() => setSearchName('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: isDark ? '#6b7280' : '#9ca3af', fontSize: '12px', padding: 0 }}>✕</button>}
              </div>

              {/* Filter toggle */}
              <button onClick={() => setShowFilter(f => !f)} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: `1.5px solid ${isDark ? '#3a3a5a' : '#e0e2ef'}`, background: showFilter ? (isDark ? '#252d4a' : '#eef1fb') : 'transparent', color: showFilter ? (isDark ? '#90b4ff' : '#1d4ed8') : (isDark ? '#9ca3af' : '#64748b'), fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
                <IconFilter /> Filter
              </button>

              {/* Add button */}
              <button
                onClick={() => setOpenDialog(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)', color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", boxShadow: '0 3px 10px rgba(29,78,216,0.3)', whiteSpace: 'nowrap' }}
              >
                <IconPlus /> Add Company
              </button>
            </div>
          </div>

          {/* Filter Row */}
          {showFilter && (
            <div style={{ background: isDark ? '#1a1d2e' : '#f8faff', border: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`, borderTop: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderBottom: 'none', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '7px 12px', borderRadius: '8px', border: `1.5px solid ${isDark ? '#3a3a5a' : '#e0e2ef'}`, background: isDark ? '#252540' : '#fff', minWidth: '180px' }}>
                <span style={{ color: isDark ? '#6b7280' : '#9ca3af', fontSize: '12px' }}>🌐</span>
                <input placeholder="Filter by domain…" value={searchDomain} onChange={(e) => { setSearchDomain(e.target.value); setPage(1); }} style={{ border: 'none', background: 'none', outline: 'none', fontSize: '13px', color: isDark ? '#e0e0ff' : '#1f2937', fontFamily: "'DM Sans',sans-serif", width: '100%' }} />
              </div>

              <select value={filterActive} onChange={(e) => { setFilterActive(e.target.value); setPage(1); }} style={{ ...inputSt, width: 'auto', minWidth: '120px' }}>
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>

              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ ...inputSt, width: 'auto', minWidth: '140px' }}>
                <option value="createdAt">Date Created</option>
                <option value="company_name">Company Name</option>
                <option value="company_domain">Domain</option>
              </select>

              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} style={{ ...inputSt, width: 'auto', minWidth: '100px' }}>
                <option value="desc">Desc</option>
                <option value="asc">Asc</option>
              </select>

              <button onClick={clearFilters} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: `1.5px solid ${isDark ? '#3a3a5a' : '#e0e2ef'}`, background: 'transparent', color: isDark ? '#9ca3af' : '#64748b', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
                Clear
              </button>
            </div>
          )}

          {/* Content */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            background: isDark ? '#1e2132' : '#fff',
            border: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`,
            borderTop: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
            borderRadius: showFilter ? '0' : '0',
            padding: '20px',
          }}>
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress size={48} style={{ color: isDark ? '#60a5fa' : '#2563eb' }} />
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#ef4444' }}>
                <p style={{ fontSize: '14px', marginBottom: '12px' }}>{typeof error === 'string' ? error : 'Failed to load companies'}</p>
                <button onClick={fetchCompanies} style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>Retry</button>
              </div>
            ) : companiesList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏢</div>
                <p style={{ fontSize: '15px', fontWeight: 600, color: isDark ? '#f1f5f9' : '#0f172a', marginBottom: '6px' }}>No companies found</p>
                <p style={{ fontSize: '13px', color: isDark ? '#6b7280' : '#94a3b8', marginBottom: '20px' }}>
                  {searchName || searchDomain || filterActive ? 'Try adjusting your filters' : 'Get started by adding your first company'}
                </p>
                {!searchName && !searchDomain && !filterActive ? (
                  <button onClick={() => setOpenDialog(true)} style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 700, fontFamily: "'DM Sans',sans-serif" }}>Add Your First Company</button>
                ) : (
                  <button onClick={clearFilters} style={{ padding: '10px 24px', borderRadius: '8px', border: `1.5px solid ${isDark ? '#3a3a5a' : '#e0e2ef'}`, background: 'transparent', color: isDark ? '#e0e0ff' : '#1f2937', cursor: 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>Clear Filters</button>
                )}
              </div>

            ) : viewMode === 'grid' ? (

              /* ── Grid View ── */
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '16px' }}>
                {companiesList.map((company, i) => (
                  <div
                    key={company._id}
                    className="domain-card"
                    style={{
                      background: isDark ? '#151827' : '#f8fafc',
                      border: `1px solid ${isDark ? '#2a2f45' : '#e2e8f0'}`,
                      borderRadius: '14px',
                      padding: '22px 18px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '10px',
                      position: 'relative',
                      transition: 'all 0.25s',
                      animationDelay: `${i * 40}ms`,
                    }}
                  >
                    {/* Status badge */}
                    <div style={{
                      position: 'absolute', top: '12px', right: '12px',
                      fontSize: '10px', fontWeight: 700, padding: '3px 9px', borderRadius: '999px',
                      background: company.is_active ? (isDark ? '#14402a' : '#dcfce7') : (isDark ? '#2a1a1a' : '#fef2f2'),
                      color: company.is_active ? (isDark ? '#4ade80' : '#16a34a') : (isDark ? '#f87171' : '#dc2626'),
                    }}>
                      {company.is_active ? 'Active' : 'Inactive'}
                    </div>

                    {/* Avatar */}
                    <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '22px', fontWeight: 800, fontFamily: "'DM Sans',sans-serif" }}>
                      {company.company_name?.slice(0, 2).toUpperCase() || 'CO'}
                    </div>

                    {/* Name */}
                    <div style={{ fontSize: '14px', fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', textAlign: 'center', wordBreak: 'break-word' }}>
                      {company.company_name}
                    </div>

                    {/* Domain */}
                    <div style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '6px', background: isDark ? '#1d2040' : '#f0f4ff', color: isDark ? '#7da4ff' : '#1d4ed8', fontFamily: 'monospace', wordBreak: 'break-all', textAlign: 'center' }}>
                      {company.company_domain}
                    </div>

                    {/* Date */}
                    <div style={{ fontSize: '11px', color: isDark ? '#6b7280' : '#94a3b8' }}>
                      {new Date(company.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                      <button className="ap-action-btn" onClick={() => handleEdit(company)} title="Edit"><IconEdit /></button>
                      <button className="ap-action-btn red" onClick={() => handleDeleteClick(company)} title="Delete"><IconTrash /></button>
                    </div>
                  </div>
                ))}
              </div>

            ) : (

              /* ── List View ── */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* List header */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr 80px', gap: '12px', padding: '8px 14px', fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: isDark ? '#6b7280' : '#94a3b8' }}>
                  <span>Company</span><span>Domain</span><span>Status</span><span>Created</span><span>Actions</span>
                </div>
                {companiesList.map((company, i) => (
                  <div
                    key={company._id}
                    className="list-row"
                    style={{
                      display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr 80px', gap: '12px',
                      padding: '12px 14px', borderRadius: '10px',
                      border: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`,
                      background: isDark ? '#151827' : '#f8fafc',
                      alignItems: 'center', transition: 'all 0.15s', cursor: 'default',
                    }}
                  >
                    {/* Company name */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '12px', fontWeight: 800, flexShrink: 0 }}>
                        {company.company_name?.slice(0, 2).toUpperCase() || 'CO'}
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#e0e0ff' : '#0d1b3e' }}>{company.company_name}</span>
                    </div>
                    {/* Domain */}
                    <div style={{ fontSize: '12px', fontFamily: 'monospace', color: isDark ? '#7da4ff' : '#1d4ed8' }}>{company.company_domain}</div>
                    {/* Status */}
                    <div>
                      <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '999px', background: company.is_active ? (isDark ? '#14402a' : '#dcfce7') : (isDark ? '#2a1a1a' : '#fef2f2'), color: company.is_active ? (isDark ? '#4ade80' : '#16a34a') : (isDark ? '#f87171' : '#dc2626') }}>
                        {company.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {/* Date */}
                    <div style={{ fontSize: '12px', color: isDark ? '#6b7280' : '#94a3b8' }}>
                      {new Date(company.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button className="ap-action-btn" onClick={() => handleEdit(company)} title="Edit"><IconEdit /></button>
                      <button className="ap-action-btn red" onClick={() => handleDeleteClick(company)} title="Delete"><IconTrash /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Pagination ── */}
          {companiesList.length > 0 && (
            <div style={{
              position: 'sticky', bottom: 0, zIndex: 5, flexShrink: 0,
              background: isDark ? '#1e2132' : '#fff',
              borderTop: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`,
              borderLeft: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`,
              borderRight: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`,
              borderRadius: '0 0 14px 14px',
              padding: '12px 20px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
              boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: isDark ? '#9ca3af' : '#64748b' }}>
                <span>Items per page:</span>
                <select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }} style={{ ...inputSt, width: 'auto', padding: '5px 8px', fontSize: '13px' }}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span>Showing <strong>{(page - 1) * rowsPerPage + 1}–{Math.min(page * rowsPerPage, paginationData.total || companiesList.length)}</strong> of <strong>{paginationData.total || companiesList.length}</strong></span>
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                {[
                  { label: '⟨|', action: () => setPage(1), disabled: page === 1 },
                  { label: '‹',  action: () => setPage(p => Math.max(1, p - 1)), disabled: page === 1 },
                  { label: '›',  action: () => setPage(p => Math.min(totalPages, p + 1)), disabled: page === totalPages || totalPages === 0 },
                  { label: '|⟩', action: () => setPage(totalPages), disabled: page === totalPages || totalPages === 0 },
                ].map(({ label, action, disabled }) => (
                  <button key={label} onClick={action} disabled={disabled} style={{ width: '32px', height: '32px', borderRadius: '7px', border: `1px solid ${isDark ? '#2d3248' : '#e2e8f0'}`, background: 'transparent', color: disabled ? (isDark ? '#3a3a5a' : '#d1d5db') : (isDark ? '#9ca3af' : '#64748b'), cursor: disabled ? 'default' : 'pointer', fontSize: '13px', fontFamily: "'DM Sans',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{label}</button>
                ))}
              </div>
            </div>
          )}

        </section>

        {/* Footer */}
        <footer style={{ flexShrink: 0, padding: '14px 20px', borderTop: `1px solid ${isDark ? '#2a2f45' : '#e8eaf0'}`, fontSize: '12px', color: isDark ? '#4b5563' : '#9ca3af', fontFamily: "'DM Sans', sans-serif", marginTop: '20px' }}>
          <span>© 2026 EstateCore CRM. Smart CRM that connects customers, data, and sales in one safe place.</span>
        </footer>

      </div>

      {/* ── Add/Edit Dialog ── */}
      {openDialog && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', animation: 'pm-fade 0.2s ease both' }}
          onClick={(e) => { if (e.target === e.currentTarget) handleCloseDialog(); }}
        >
          <div style={{ width: '100%', maxWidth: '480px', margin: '0 20px', borderRadius: '20px', overflow: 'hidden', background: isDark ? 'linear-gradient(135deg,#1a1f2e,#141829)' : '#fff', boxShadow: '0 40px 80px rgba(0,0,0,0.3)', animation: 'pm-slide 0.25s cubic-bezier(0.34,1.2,0.64,1) both', color: isDark ? '#E4E8F5' : '#1C2340', position: 'relative' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)', padding: '24px 28px' }}>
              <p style={{ fontSize: '18px', fontWeight: 700, color: '#fff', margin: 0 }}>{selectedCompany ? 'Edit Company' : 'Add New Company'}</p>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.65)', margin: '4px 0 0' }}>Fill in the company domain details below</p>
            </div>
            {/* Close */}
            <button onClick={handleCloseDialog} style={{ position: 'absolute', top: '16px', right: '16px', width: '32px', height: '32px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <IconClose />
            </button>
            {/* Body */}
            <div style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div>
                <label style={labelSt}>Company Name</label>
                <input
                  autoFocus
                  style={dialogInputSt(!!formErrors.company_name)}
                  value={formData.company_name}
                  onChange={(e) => { setFormData({ ...formData, company_name: e.target.value }); if (formErrors.company_name) setFormErrors(p => ({ ...p, company_name: '' })); }}
                  placeholder="e.g., Acme Corporation"
                  onFocus={(e) => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.12)'; }}
                  onBlur={(e) => { e.target.style.borderColor = formErrors.company_name ? '#ef4444' : (isDark ? '#2d3248' : '#e2e8f0'); e.target.style.boxShadow = 'none'; }}
                />
                {formErrors.company_name && <p style={{ fontSize: '12px', color: '#ef4444', margin: '5px 0 0' }}>{formErrors.company_name}</p>}
              </div>
              <div>
                <label style={labelSt}>Company Domain</label>
                <input
                  style={dialogInputSt(!!formErrors.company_domain)}
                  value={formData.company_domain}
                  onChange={(e) => { setFormData({ ...formData, company_domain: e.target.value }); if (formErrors.company_domain) setFormErrors(p => ({ ...p, company_domain: '' })); }}
                  placeholder="e.g., acme.com"
                  onFocus={(e) => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.12)'; }}
                  onBlur={(e) => { e.target.style.borderColor = formErrors.company_domain ? '#ef4444' : (isDark ? '#2d3248' : '#e2e8f0'); e.target.style.boxShadow = 'none'; }}
                />
                {formErrors.company_domain && <p style={{ fontSize: '12px', color: '#ef4444', margin: '5px 0 0' }}>{formErrors.company_domain}</p>}
              </div>
              <div style={{ padding: '14px 16px', borderRadius: '10px', background: isDark ? 'rgba(37,99,235,0.08)' : 'rgba(37,99,235,0.04)', border: `1px solid ${isDark ? 'rgba(37,99,235,0.2)' : 'rgba(37,99,235,0.15)'}` }}>
                <FormControlLabel
                  control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} color="primary" size="small" />}
                  label={
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#f1f5f9' : '#0f172a', fontFamily: "'DM Sans',sans-serif" }}>Active Status</div>
                      <div style={{ fontSize: '11px', color: isDark ? '#6b7280' : '#94a3b8', fontFamily: "'DM Sans',sans-serif" }}>Inactive companies won't appear in active lists</div>
                    </div>
                  }
                />
              </div>
            </div>
            {/* Footer */}
            <div style={{ padding: '0 28px 28px', display: 'flex', gap: '10px' }}>
              <button onClick={handleCloseDialog} disabled={isSubmitting} style={{ flex: 1, padding: '13px', borderRadius: '10px', border: `1.5px solid ${isDark ? '#3A4570' : '#E8EAF0'}`, background: isDark ? '#252D4A' : '#F7F8FC', color: isDark ? '#E4E8F5' : '#1C2340', fontFamily: "'DM Sans',sans-serif", fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSubmit} disabled={isSubmitting} style={{ flex: 1, padding: '13px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)', color: '#fff', fontFamily: "'DM Sans',sans-serif", fontSize: '14px', fontWeight: 700, cursor: isSubmitting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: isSubmitting ? 0.7 : 1 }}>
                {isSubmitting ? <><div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'pm-spin 0.7s linear infinite' }} />Processing…</> : (selectedCompany ? 'Update Company' : 'Create Company')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Dialog ── */}
      {openDeleteDialog && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', animation: 'pm-fade 0.2s ease both' }}
          onClick={(e) => { if (!isSubmitting && e.target === e.currentTarget) setOpenDeleteDialog(false); }}
        >
          <div style={{ width: '100%', maxWidth: '440px', margin: '0 20px', borderRadius: '20px', padding: '36px 32px 32px', background: isDark ? 'linear-gradient(135deg,#1a1f2e,#141829)' : '#fff', boxShadow: '0 40px 80px rgba(0,0,0,0.3)', animation: 'pm-slide 0.25s cubic-bezier(0.34,1.2,0.64,1) both', color: isDark ? '#E4E8F5' : '#1C2340', textAlign: 'center', position: 'relative' }}>
            <button onClick={() => setOpenDeleteDialog(false)} style={{ position: 'absolute', top: '14px', right: '14px', width: '32px', height: '32px', borderRadius: '8px', border: `1px solid ${isDark ? '#3A4570' : '#E8EAF0'}`, background: isDark ? '#252D4A' : '#F7F8FC', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDark ? '#9099c0' : '#7B829A' }}>
              <IconClose />
            </button>
            <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(229,57,53,0.15)', border: '2px solid rgba(229,57,53,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: '#E53935' }}>
              <IconWarning />
            </div>
            <p style={{ fontSize: '19px', fontWeight: 700, margin: '0 0 8px', letterSpacing: '-0.3px' }}>Delete Company?</p>
            <p style={{ fontSize: '13px', color: isDark ? '#7B829A' : '#6b7280', margin: '0 0 24px', lineHeight: 1.6 }}>
              You are about to delete <strong>"{selectedCompany?.company_name}"</strong>.<br />This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setOpenDeleteDialog(false)} disabled={isSubmitting} style={{ flex: 1, padding: '13px', borderRadius: '10px', border: `1.5px solid ${isDark ? '#3A4570' : '#E8EAF0'}`, background: isDark ? '#252D4A' : '#F7F8FC', color: isDark ? '#E4E8F5' : '#1C2340', fontFamily: "'DM Sans',sans-serif", fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDelete} disabled={isSubmitting} style={{ flex: 1, padding: '13px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#E53935,#c62828)', color: '#fff', fontFamily: "'DM Sans',sans-serif", fontSize: '14px', fontWeight: 700, cursor: isSubmitting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: isSubmitting ? 0.7 : 1 }}>
                {isSubmitting ? <><div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'pm-spin 0.7s linear infinite' }} />Deleting…</> : <><IconTrash /> Delete Company</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Snackbar ── */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};